import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { motion, useInView } from "framer-motion";
import { ArrowRight, FolderOpen, ChevronDown } from "lucide-react";

const ORBIT_NODES = ["IDEA", "VALIDATION", "MODEL", "BRAND", "TECH", "LAUNCH", "GROWTH"];
const ORBIT_NODES_2 = ["AI", "APPS", "FINTECH", "E-COM", "LOGISTICS", "MFG"];

function HeroScene() {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    } catch {
      return;
    }
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const isMobile = window.innerWidth < 768;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(55, mount.clientWidth / mount.clientHeight, 0.1, 100);
    camera.position.z = 7.6;

    const group = new THREE.Group();
    scene.add(group);

    // Particle sphere — the idea/business ecosystem core
    const N = isMobile ? 900 : 2200;
    const pos = new Float32Array(N * 3);
    const col = new Float32Array(N * 3);
    const golden = Math.PI * (3 - Math.sqrt(5));
    const saffron = new THREE.Color("#FF8800");
    const blue = new THREE.Color("#00B0FF");
    const green = new THREE.Color("#00E676");
    for (let i = 0; i < N; i++) {
      const y = 1 - (i / (N - 1)) * 2;
      const r = Math.sqrt(Math.max(0, 1 - y * y));
      const th = golden * i;
      pos[i * 3] = Math.cos(th) * r * 2.4;
      pos[i * 3 + 1] = y * 2.4;
      pos[i * 3 + 2] = Math.sin(th) * r * 2.4;
      const c = i % 17 === 0 ? saffron : i % 23 === 0 ? green : blue;
      col[i * 3] = c.r;
      col[i * 3 + 1] = c.g;
      col[i * 3 + 2] = c.b;
    }
    const sphereGeo = new THREE.BufferGeometry();
    sphereGeo.setAttribute("position", new THREE.BufferAttribute(pos, 3));
    sphereGeo.setAttribute("color", new THREE.BufferAttribute(col, 3));
    const sphere = new THREE.Points(
      sphereGeo,
      new THREE.PointsMaterial({
        size: 0.035,
        vertexColors: true,
        transparent: true,
        opacity: 0.9,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      })
    );
    group.add(sphere);

    // Wireframe icosahedron core
    const core = new THREE.LineSegments(
      new THREE.EdgesGeometry(new THREE.IcosahedronGeometry(1.35, 1)),
      new THREE.LineBasicMaterial({ color: 0xff8800, transparent: true, opacity: 0.32 })
    );
    group.add(core);

    const glowCore = new THREE.Mesh(
      new THREE.SphereGeometry(0.8, 32, 32),
      new THREE.MeshBasicMaterial({ color: 0xff8800, transparent: true, opacity: 0.07 })
    );
    group.add(glowCore);

    // Orbital rings with satellites
    const ringGroup1 = new THREE.Group();
    ringGroup1.rotation.x = Math.PI / 2.25;
    const ring1 = new THREE.Mesh(
      new THREE.TorusGeometry(3.35, 0.011, 8, 140),
      new THREE.MeshBasicMaterial({ color: 0x00b0ff, transparent: true, opacity: 0.32 })
    );
    ringGroup1.add(ring1);
    group.add(ringGroup1);

    const ringGroup2 = new THREE.Group();
    ringGroup2.rotation.x = Math.PI / 1.65;
    ringGroup2.rotation.y = 0.5;
    const ring2 = new THREE.Mesh(
      new THREE.TorusGeometry(3.95, 0.009, 8, 140),
      new THREE.MeshBasicMaterial({ color: 0xff8800, transparent: true, opacity: 0.22 })
    );
    ringGroup2.add(ring2);
    group.add(ringGroup2);

    const satGeo = new THREE.SphereGeometry(0.075, 12, 12);
    const sats1 = [];
    for (let i = 0; i < 7; i++) {
      const m = new THREE.Mesh(
        satGeo,
        new THREE.MeshBasicMaterial({ color: i % 2 ? 0xff8800 : 0x00b0ff })
      );
      sats1.push({ mesh: m, angle: (i / 7) * Math.PI * 2, speed: 0.22 + (i % 3) * 0.05 });
      ringGroup1.add(m);
    }
    const sats2 = [];
    for (let i = 0; i < 5; i++) {
      const m = new THREE.Mesh(satGeo, new THREE.MeshBasicMaterial({ color: 0x00e676 }));
      sats2.push({ mesh: m, angle: (i / 5) * Math.PI * 2, speed: 0.15 });
      ringGroup2.add(m);
    }

    // Ambient starfield
    const starN = isMobile ? 250 : 600;
    const starPos = new Float32Array(starN * 3);
    for (let i = 0; i < starN * 3; i++) starPos[i] = (Math.random() - 0.5) * 26;
    const starGeo = new THREE.BufferGeometry();
    starGeo.setAttribute("position", new THREE.BufferAttribute(starPos, 3));
    const stars = new THREE.Points(
      starGeo,
      new THREE.PointsMaterial({ size: 0.02, color: 0x94a3b8, transparent: true, opacity: 0.5, depthWrite: false })
    );
    scene.add(stars);

    let mx = 0;
    let my = 0;
    let smx = 0;
    let smy = 0;
    const onMove = (e) => {
      mx = e.clientX / window.innerWidth - 0.5;
      my = e.clientY / window.innerHeight - 0.5;
    };
    const onResize = () => {
      camera.aspect = mount.clientWidth / mount.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mount.clientWidth, mount.clientHeight);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("resize", onResize);

    let visible = true;
    const io = new IntersectionObserver(([entry]) => (visible = entry.isIntersecting), { threshold: 0 });
    io.observe(mount);

    let raf;
    let t = Math.random() * 20;
    const dt = 0.016;
    const animate = () => {
      raf = requestAnimationFrame(animate);
      if (!visible) return;
      if (!reduced) t += 0.0038;
      smx += (mx - smx) * 0.04;
      smy += (my - smy) * 0.04;

      group.rotation.y = t + smx * 0.9;
      group.rotation.x = 0.22 + smy * 0.5;
      core.rotation.y = -t * 1.5;
      core.rotation.x = t * 0.8;
      sphere.rotation.y = t * 0.35;
      stars.rotation.y = t * 0.03;

      for (const s of sats1) {
        if (!reduced) s.angle += s.speed * dt;
        s.mesh.position.set(Math.cos(s.angle) * 3.35, 0, Math.sin(s.angle) * 3.35);
      }
      for (const s of sats2) {
        if (!reduced) s.angle -= s.speed * dt;
        s.mesh.position.set(Math.cos(s.angle) * 3.95, 0, Math.sin(s.angle) * 3.95);
      }
      glowCore.material.opacity = 0.055 + Math.sin(t * 3) * 0.025;

      renderer.render(scene, camera);
    };
    animate();

    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("resize", onResize);
      [sphereGeo, starGeo, satGeo, core.geometry, glowCore.geometry, ring1.geometry, ring2.geometry].forEach((g) => g.dispose());
      [sphere.material, stars.material, core.material, glowCore.material, ring1.material, ring2.material].forEach((m) => m.dispose());
      sats1.concat(sats2).forEach((s) => s.mesh.material.dispose());
      mount.removeChild(renderer.domElement);
      renderer.dispose();
    };
  }, []);

  return <div ref={mountRef} data-testid="hero-3d-scene" className="absolute inset-0 w-full h-full" />;
}

function Counter({ to, suffix }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const dur = 1600;
    const tick = (now) => {
      const p = Math.min((now - start) / dur, 1);
      setVal(Math.round(to * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, to]);

  return (
    <span ref={ref} className="font-display font-extrabold text-2xl sm:text-3xl text-white">
      {val}
      <span className="text-[#FF8800]">{suffix}</span>
    </span>
  );
}

const STATS = [
  { to: 120, suffix: "+", label: "Ideas Validated" },
  { to: 35, suffix: "+", label: "Ventures Built" },
  { to: 12, suffix: "", label: "Cities Reached" },
  { to: 800, suffix: "+", label: "Jobs Enabled" },
];

export function Hero() {
  return (
    <section id="home" data-testid="hero-section" className="cinema relative min-h-screen flex items-center overflow-hidden bg-grid-tech">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(0,176,255,0.06),transparent_60%)]" />
      <HeroScene />

      {/* Orbiting ecosystem labels */}
      <div className="absolute inset-0 pointer-events-none hidden lg:flex items-center justify-center">
        <div className="relative w-[880px] h-[880px]">
          <div className="orbit-ring absolute inset-0 rounded-full border border-white/5">
            {ORBIT_NODES.map((n, i) => (
              <div key={n} className="absolute inset-0" style={{ transform: `rotate(${(360 / ORBIT_NODES.length) * i}deg)` }}>
                <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                  <div className="orbit-chip">
                    <span
                      className="glass inline-block px-3 py-1.5 rounded-full font-mono-tech text-[10px] tracking-[0.2em] text-slate-200 whitespace-nowrap"
                      style={{ transform: `rotate(${-(360 / ORBIT_NODES.length) * i}deg)` }}
                    >
                      {n}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="orbit-ring-2 absolute inset-[110px] rounded-full border border-dashed border-[#00B0FF]/10">
            {ORBIT_NODES_2.map((n, i) => (
              <div key={n} className="absolute inset-0" style={{ transform: `rotate(${(360 / ORBIT_NODES_2.length) * i}deg)` }}>
                <div className="absolute -top-2.5 left-1/2 -translate-x-1/2">
                  <div className="orbit-chip-2">
                    <span
                      className="inline-block px-2.5 py-1 rounded-full bg-[#00B0FF]/10 border border-[#00B0FF]/25 font-mono-tech text-[9px] tracking-[0.18em] text-[#00B0FF] whitespace-nowrap"
                      style={{ transform: `rotate(${-(360 / ORBIT_NODES_2.length) * i}deg)` }}
                    >
                      {n}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 w-full">
        <div className="max-w-3xl">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-mono-tech text-xs sm:text-sm tracking-[0.35em] text-[#FF8800] uppercase mb-6"
            data-testid="hero-eyebrow"
          >
            India's Venture Creation Engine
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="font-display text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.02] text-gradient-hero"
            data-testid="hero-headline"
          >
            BUILD. LAUNCH.{" "}
            <br />
            <span className="text-gradient-saffron">TRANSFORM.</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-6 text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl"
            data-testid="hero-subheadline"
          >
            LaunchLab transforms ideas into businesses, businesses into opportunities, and
            opportunities into India's next generation of growth.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.45 }}
            className="mt-10 flex flex-wrap gap-4"
          >
            <a
              href="#about"
              data-testid="hero-cta-explore"
              className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-to-r from-[#FF8800] to-[#ff5e00] text-[#06080E] font-semibold text-sm tracking-wide transition-transform duration-300 hover:scale-105 shadow-[0_0_40px_rgba(255,136,0,0.35)]"
            >
              EXPLORE LAUNCHLAB
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </a>
            <a
              href="#projects"
              data-testid="hero-cta-projects"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full glass text-slate-100 font-semibold text-sm tracking-wide transition-all duration-300 hover:border-[#00B0FF]/50 hover:shadow-[0_0_30px_rgba(0,176,255,0.2)]"
            >
              <FolderOpen className="w-4 h-4 text-[#00B0FF]" />
              VIEW COMPLETED PROJECTS
            </a>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.65 }}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-3xl"
          data-testid="hero-stats"
        >
          {STATS.map((s) => (
            <div key={s.label} className="glass rounded-xl px-5 py-4">
              <Counter to={s.to} suffix={s.suffix} />
              <p className="text-xs text-slate-400 mt-1 font-mono-tech tracking-wider uppercase">{s.label}</p>
            </div>
          ))}
        </motion.div>
      </div>

      <a
        href="#about"
        data-testid="hero-scroll-down"
        aria-label="Scroll to What is LaunchLab"
        className="absolute bottom-7 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-1.5 text-slate-500 hover:text-[#FF8800] transition-colors"
      >
        <span className="font-mono-tech text-[9px] tracking-[0.35em] uppercase">Scroll</span>
        <ChevronDown className="w-5 h-5 animate-bounce" />
      </a>

      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-b from-transparent to-[#06080E] pointer-events-none" />
    </section>
  );
}
